import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CreditDialogComponent } from '../../shared/components/credits-dialog/credits-dialog.component';
import { ApiService } from '../../core/services/api.service';
import { MatTableModule } from '@angular/material/table';
import { Credit } from '../../core/models/credit';
import { PageEvent } from '@angular/material/paginator';
import { MatPaginatorModule } from '@angular/material/paginator';
import { NavbarComponent } from '../../shared/components/navbar/navbar.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { OrganizationDialogComponent } from '../../shared/components/organization-dialog/organization-dialog.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { EmailCreditsDialogComponent } from '../../shared/components/email-credits-dialog/email-credits-dialog.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { NavigationRailComponent } from '../../shared/components/navigation-rail/navigation-rail.component';
import { AccountManagementDialogComponent, ManagedAccount } from './account-management-dialog.component';


interface User {
    id: number;
    name: string;
    email: string;
    reportsGenerated: number;
    moneySpent: number;
    creditsLeft: number;
    credits: Credit[];
    organizations?: any[];
}

interface UserResponse {
  start: number;
  limit: number;
  length: number;
  users: User[];
  total: number;
}

interface Organization {
    id: number;
    name: string;
    creditsLeft: number;
    credits: Credit[];
}

@Component({
  selector: 'app-accounts',
  templateUrl: './account.component.html',
  imports: [
    MatTableModule, 
    MatPaginatorModule, 
    NavbarComponent, 
    MatTabsModule, 
    CommonModule, 
    MatIconModule, 
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    FooterComponent,
    NavigationRailComponent
  ],
  styleUrls: ['./account.component.scss'],
  standalone: true
})
export class AccountsComponent implements OnInit {
  users: User[] = [];
  organizations: Organization[] = [];
  displayedColumns: string[] = ['user', 'reportsGenerated', 'actions'];
  displayedColumnsOrg: string[] = ['name'];
  pageSize = 10;
  pageIndex = 0;
  totalUsers = 0;
  totalOrganizations = 0;
  activeTab = 'users';
  emailForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email])
  });
  accountQuery = '';
  statusFilter = '';
  readonly mockAccounts = [
    { email: 'analyst@client.example', organization: 'Client Maritime Team', role: 'Admin', plan: 'Maritime Unlimited', created: '03 Jun 2026', reports: 31, status: 'Active' },
    { email: 'ops@client.example', organization: 'Client Maritime Team', role: 'Analyst', plan: 'Maritime Unlimited', created: '04 Jun 2026', reports: 18, status: 'Active' },
    { email: 'review@client.example', organization: 'Client Maritime Team', role: 'Reviewer', plan: 'Maritime Unlimited', created: '05 Jun 2026', reports: 7, status: 'Active' },
    { email: 'viewer@client.example', organization: 'Client Maritime Team', role: 'Viewer', plan: 'Maritime Unlimited', created: '11 Jul 2026', reports: 0, status: 'Invited' }
  ];

  constructor(private apiService: ApiService, private dialog: MatDialog, private router: Router) {}

  ngOnInit() {
    this.loadUsers();
  }

  get visibleAccounts(): Array<ManagedAccount & { created: string; reports: number }> {
    const accounts = this.users.length
      ? this.users.map((user, index) => ({
          email: user.email,
          organization: user.organizations?.[0]?.name || 'Client Maritime Team',
          role: index === 0 ? 'Admin' : 'Analyst',
          plan: 'Maritime Unlimited',
          created: '—', reports: user.reportsGenerated, status: 'Active', user
        }))
      : this.mockAccounts;
    const query = this.accountQuery.trim().toLowerCase();
    return accounts.filter(account =>
      (!query || [account.email, account.organization, account.role, account.plan].join(' ').toLowerCase().includes(query)) &&
      (!this.statusFilter || account.status === this.statusFilter)
    );
  }

  clearFilters(): void { this.accountQuery = ''; this.statusFilter = ''; }

  openManageAccount(account: ManagedAccount): void {
    this.dialog.open(AccountManagementDialogComponent, {
      width: '720px',
      maxWidth: 'calc(100vw - 2rem)',
      panelClass: 'tevuna-account-modal',
      backdropClass: 'tevuna-account-modal-backdrop',
      data: { ...account, user: account.user ?? { email: account.email, credits: [] } }
    });
  }

  onTabChange(event: MatTabChangeEvent) {
    this.pageIndex = 0;
    this.activeTab = event.index === 0 ? 'users' : 'organizations';
    this.loadData();
  }

  loadData() {
    if (this.activeTab === 'users') {
      this.loadUsers();
    } else {
      this.loadOrganizations();
    }
  }

  loadOrganizations() {
    this.apiService.getOrganizations().subscribe((response: any) => {
      this.organizations = response;
    });
  }

  loadUsers() {
    this.apiService.getUsersReport().subscribe((response: UserResponse) => {
      this.users = response.users;
      this.totalUsers = response.total;
    });
  }

  handlePageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  openCreditDialog(user: User) {
    const dialogRef = this.dialog.open(CreditDialogComponent, {
      width: '1000px',
      height: '500px',
      data: { 
        entity: user,
        entityType: 'user'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  viewOrganization(org: Organization) {
    this.router.navigate(['/organizations', org.id]);
  }

  createOrganization() {
    const dialogRef = this.dialog.open(OrganizationDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.apiService.createOrganization(result).subscribe({
          next: () => {
            this.loadOrganizations();
          },
          error: (error) => {
            console.error('Error creating organization:', error);
          }
        });
      }
    });
  }

  openEmailCreditsDialog() {
    const emailDialogRef = this.dialog.open(EmailCreditsDialogComponent, {
      width: '400px'
    });

    emailDialogRef.afterClosed().subscribe(email => {
      if (email) {
        const creditDialogRef = this.dialog.open(CreditDialogComponent, {
          width: '1000px',
          height: '500px',
          data: { 
            entity: {
              email: email,
              credits: []
            },
            entityType: 'user'
          }
        });

        creditDialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.loadData();
          }
        });
      }
    });
  }
}
